<template>
  <div class="p-6 bg-gray-50 dark:bg-gray-900/50 min-h-screen animate-fadeIn">
    <!-- Header -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-blue-600 rounded-full"></div>
          <span class="text-xs font-black text-blue-600 tracking-widest uppercase">Centro de Mensajes</span>
        </div>
        <h1 class="text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-3">
          Notificaciones del Sistema
          <span v-if="noLeidas > 0" class="px-3 py-1 bg-red-100 text-red-600 text-xs font-black rounded-full border border-red-200">
            {{ noLeidas }} pendientes
          </span>
        </h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm font-bold mt-1">Historial detallado de actividades y alertas críticas.</p>
      </div>

      <div class="flex gap-3">
        <button 
          @click="marcarTodasLeidas"
          class="px-5 py-2.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-100 transition-all border border-gray-200 dark:border-gray-700 shadow-sm flex items-center gap-2"
        >
          <Icon icon="mdi:check-all" class="text-lg" />
          Marcar Todo Leído
        </button>
        <button 
          @click="limpiarHistorial"
          class="px-5 py-2.5 bg-rose-50 text-rose-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-rose-100 transition-all border border-rose-100 shadow-sm flex items-center gap-2"
        >
          <Icon icon="mdi:trash-can-outline" class="text-lg" />
          Limpiar Historial
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 p-4 rounded-3xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6 flex flex-wrap gap-3 items-center">
      <span class="text-xs font-black text-gray-400 uppercase tracking-widest ml-2">Filtrar por:</span>
      <button 
        v-for="f in filtros" 
        :key="f.id"
        @click="filtroActivo = f.id"
        :class="[
          'px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all',
          filtroActivo === f.id 
            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
            : 'bg-gray-50 dark:bg-gray-900/50 text-gray-500 hover:bg-gray-100'
        ]"
      >
        {{ f.nombre }}
      </button>
    </div>

    <!-- Lista -->
    <div class="space-y-4 max-w-4xl mx-auto">
      <div v-if="notificacionesFiltradas.length === 0" class="text-center py-20 bg-white dark:bg-gray-800 rounded-[3rem] border border-dashed border-gray-200 dark:border-gray-700">
        <Icon icon="mdi:bell-off-outline" class="text-7xl text-gray-200 dark:text-gray-700 mx-auto mb-4" />
        <h3 class="text-xl font-black text-gray-400 uppercase tracking-widest">No hay notificaciones</h3>
        <p class="text-gray-400 font-bold mt-2">Tu historial está completamente limpio.</p>
      </div>

      <div 
        v-for="notif in notificacionesFiltradas" 
        :key="notif.id"
        class="group relative bg-white dark:bg-gray-800 p-6 rounded-[2rem] border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-xl transition-all duration-300"
        :class="{ 'border-l-[6px] border-l-blue-500': !notif.leida }"
      >
        <div class="flex gap-6 items-start">
          <div class="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-gray-900/50 flex items-center justify-center text-4xl shadow-inner group-hover:scale-110 transition-transform">
            {{ notif.icono || '🔔' }}
          </div>
          
          <div class="flex-1 min-w-0">
            <div class="flex justify-between items-start mb-1">
              <div>
                <span class="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest block mb-0.5">
                  Módulo: {{ notif.modulo }}
                </span>
                <h3 class="text-lg font-black text-gray-900 dark:text-white leading-tight">
                  {{ notif.titulo }}
                </h3>
              </div>
              <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                {{ tiempoRelativo(notif.fecha) }}
              </span>
            </div>
            
            <p class="text-gray-600 dark:text-gray-400 font-bold text-sm mb-3">
              {{ notif.detalle }}
            </p>
            
            <div class="flex items-center gap-4">
              <span :class="[
                'px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest',
                notif.accion === 'Crítico' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
              ]">
                {{ notif.accion }}
              </span>
              <span class="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                {{ formatDateTime(notif.fecha) }}
              </span>
            </div>
          </div>

          <div class="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              v-if="!notif.leida"
              @click="marcarLeida(notif.id)"
              class="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm"
              title="Marcar como leída"
            >
              <Icon icon="mdi:check" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmación Custom (NUEVO) -->
    <ModalConfirm 
      :show="confirmModal.show"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :type="confirmModal.type"
      :icon="confirmModal.icon"
      :confirmText="confirmModal.confirmText"
      @confirm="ejecutarAccionConfirmada"
      @cancel="confirmModal.show = false"
    />
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { 
  cargarNotificaciones, 
  marcarTodasLeidas, 
  marcarLeida, 
  limpiarNotificaciones,
  tiempoRelativo 
} from "@/utils/notificaciones";
import { useToast } from "vue-toastification";
import ModalConfirm from "@/components/ModalConfirm.vue";

export default {
  name: "NotificacionesList",
  components: { Icon, ModalConfirm },
  data() {
    return {
      notificaciones: [],
      filtroActivo: 'todas',
      filtros: [
        { id: 'todas', nombre: 'Todas' },
        { id: 'pendientes', nombre: 'Pendientes' },
        { id: 'critico', nombre: 'Críticas' },
        { id: 'credito', nombre: 'Finanzas' }
      ],
      confirmModal: {
        show: false,
        title: '',
        message: '',
        type: 'primary',
        icon: '',
        confirmText: '',
        callback: null
      }
    };
  },
  computed: {
    noLeidas() {
      return this.notificaciones.filter(n => !n.leida).length;
    },
    notificacionesFiltradas() {
      let list = [...this.notificaciones];
      if (this.filtroActivo === 'pendientes') list = list.filter(n => !n.leida);
      if (this.filtroActivo === 'critico') list = list.filter(n => n.accion === 'Crítico');
      if (this.filtroActivo === 'credito') list = list.filter(n => n.modulo === 'credito');
      return list;
    }
  },
  methods: {
    tiempoRelativo,
    cargar() {
      this.notificaciones = cargarNotificaciones();
    },
    marcarTodasLeidas() {
      marcarTodasLeidas();
      this.cargar();
    },
    marcarLeida(id) {
      marcarLeida(id);
      this.cargar();
    },
    limpiarHistorial() {
      this.confirmModal = {
        show: true,
        title: 'Limpiar Historial',
        message: '¿Estás seguro de eliminar todo el historial de notificaciones? <br><br> Esta acción no se puede deshacer.',
        type: 'danger',
        icon: 'mdi:trash-can-alert',
        confirmText: 'Sí, limpiar todo',
        callback: () => {
          limpiarNotificaciones();
          this.cargar();
        }
      };
    },
    ejecutarAccionConfirmada() {
      this.confirmModal.show = false;
      if (this.confirmModal.callback) {
        this.confirmModal.callback();
      }
    },
    formatDateTime(iso) {
      const d = new Date(iso);
      return d.toLocaleString('es-VE', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  mounted() {
    this.cargar();
    window.addEventListener('notificacionNueva', this.cargar);
    window.addEventListener('notificacionesActualizadas', this.cargar);
  },
  beforeUnmount() {
    window.removeEventListener('notificacionNueva', this.cargar);
    window.removeEventListener('notificacionesActualizadas', this.cargar);
  }
};
</script>
